import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db8fvibk13ra8mig8r40";

const features = [
  {
    icon: "ri-movie-2-line",
    title: "丰富内容",
    desc: "覆盖电影、电视剧、综艺三大板块，持续更新，题材多样。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多维筛选",
    desc: "按类型、地区、年代、评分自由组合筛选，找片不再大海捞针。",
  },
  {
    icon: "ri-search-line",
    title: "快速搜索",
    desc: "关键词模糊匹配，输入片名、类型或地区即可精准定位。",
  },
  {
    icon: "ri-device-line",
    title: "多端适配",
    desc: "电脑与手机端一致体验，随时随地轻松浏览。",
  },
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useDocumentMeta({
    title: "关于我们 - 在线播放_高清免费视频播放平台",
    description: "了解在线播放平台定位、联系方式与使用及版权声明，本站为免费视频浏览演示平台。",
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK";

      if (isOk && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(String(serverMsg) || "提交失败，请稍后重试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试");
    }
  };

  return (
    <div className="pt-16 md:pt-20">
      {/* Hero */}
      <section className="relative px-4 md:px-8 py-14 md:py-20 bg-gradient-to-b from-background-100 to-background-50 overflow-hidden">
        <div className="relative z-10 max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/15 border border-accent-500/40 text-accent-400 text-xs md:text-sm font-medium mb-4">
            <i className="ri-information-line" /> 关于我们
          </span>
          <h1 className="font-heading font-extrabold text-3xl md:text-5xl text-foreground-950 leading-tight">
            让看片这件事，简单一点
          </h1>
          <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed">
            在线播放是一个以“在线播放”为品牌的高清免费视频浏览平台，面向喜爱观看电影、电视剧和综艺节目的普通用户。
            我们希望用清晰的分类、灵活的筛选和便捷的搜索，帮助每一位观众快速找到想看的内容，享受轻松的浏览体验。
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="px-4 md:px-8 py-10 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {features.map((f) => (
            <div key={f.title} className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-300/50">
              <span className="w-11 h-11 rounded-lg bg-primary-500/15 text-primary-300 flex items-center justify-center">
                <i className={`${f.icon} text-2xl`} />
              </span>
              <h3 className="mt-4 font-heading font-semibold text-base text-foreground-950">{f.title}</h3>
              <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact + statement */}
      <section className="px-4 md:px-8 pb-14 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          {/* Contact info */}
          <div>
            <h2 className="font-heading font-bold text-2xl text-foreground-950">联系我们</h2>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              如果你对平台有任何建议、合作意向，或发现内容相关问题，欢迎通过下方留言入口与我们联系。
              我们会认真阅读每一条反馈。
            </p>
            <ul className="mt-6 space-y-4">
              <li className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-md bg-background-200/70 flex items-center justify-center">
                  <i className="ri-mail-line text-accent-500" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">联系邮箱（演示）</span>
                  <span className="block text-sm text-foreground-900">contact@onlineplay-demo.com</span>
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-md bg-background-200/70 flex items-center justify-center">
                  <i className="ri-time-line text-accent-500" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">服务时间（演示）</span>
                  <span className="block text-sm text-foreground-900">周一至周五 09:00 - 18:00</span>
                </span>
              </li>
            </ul>

            <div className="mt-8 p-5 rounded-lg bg-background-100 border border-background-300/50">
              <h3 className="font-heading font-semibold text-base text-foreground-950 flex items-center gap-2">
                <i className="ri-shield-check-line text-accent-500" /> 使用及版权声明
              </h3>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                本站为演示站点，页面中出现的影片名称、海报、简介等均为虚构示例，不对应任何真实作品。
                平台不提供真实视频播放、下载或付费服务，播放按钮仅作功能展示。
                收藏功能仅保存在当前浏览器本地，清除浏览器数据后将失效。
              </p>
            </div>
          </div>

          {/* Message form */}
          <div className="p-5 md:p-7 rounded-lg bg-background-100 border border-background-300/50">
            <h2 className="font-heading font-bold text-xl text-foreground-950">留言入口</h2>
            <p className="mt-2 text-sm text-foreground-600">填写下面的信息，我们会尽快回复你（演示）。</p>

            <form data-readdy-form id="about-contact-form" onSubmit={handleSubmit} className="mt-5 space-y-4">
              <div>
                <label htmlFor="about-name" className="block text-sm text-foreground-700 mb-1.5">
                  你的称呼
                </label>
                <input
                  id="about-name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入称呼"
                  className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300/70 text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/50"
                />
              </div>

              <div>
                <label htmlFor="about-email" className="block text-sm text-foreground-700 mb-1.5">
                  邮箱
                </label>
                <input
                  id="about-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300/70 text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/50"
                />
              </div>

              <div>
                <label htmlFor="about-subject" className="block text-sm text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="about-subject"
                  name="subject"
                  type="text"
                  placeholder="想聊聊什么"
                  className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300/70 text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/50"
                />
              </div>

              <div>
                <label htmlFor="about-message" className="block text-sm text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="about-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="最多 500 字"
                  className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300/70 text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/50 resize-none"
                />
              </div>

              <input
                className="form-aux-field"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                className="w-full py-3 rounded-md font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                提交留言
              </button>

              {status === "success" && (
                <p className="flex items-center gap-2 text-sm text-accent-500">
                  <i className="ri-checkbox-circle-line" /> 留言已提交，感谢你的反馈！
                </p>
              )}
              {status === "error" && (
                <p className="flex items-center gap-2 text-sm text-primary-300">
                  <i className="ri-error-warning-line" /> {formError}
                </p>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 md:px-8 pb-16">
        <div className="relative rounded-lg overflow-hidden bg-gradient-to-r from-primary-950 via-primary-900 to-background-100 p-8 md:p-12 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">准备好开始浏览了吗？</h2>
          <p className="mt-3 text-sm md:text-base text-foreground-700">从热门推荐到分类筛选，总有一部适合你。</p>
          <div className="flex flex-wrap justify-center gap-3 mt-7">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md font-medium whitespace-nowrap bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line" /> 返回首页
            </Link>
            <Link
              to="/category/movie"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md font-medium whitespace-nowrap bg-background-200/80 text-foreground-950 hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className="ri-film-line" /> 浏览电影
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}